// Who writes to the lead (ADR 0063): the AI writes the placeholder where it
// introduces itself, and Polaris puts the first name of whoever sends it.

export const OPERATOR_PLACEHOLDER = "{operatore}";

const PLACEHOLDER_SOURCE = "\\{\\s*operatore\\s*\\}";

export function hasOperatorPlaceholder(text: string | null | undefined): boolean {
  return Boolean(text && new RegExp(PLACEHOLDER_SOURCE, "iu").test(text));
}

/** "alessio rossi" → "Alessio": the name a member signs with; null without a name. */
export function operatorFirstName(name: string | null | undefined): string | null {
  const first = name?.trim().split(/\s+/u)[0];
  if (!first) return null;
  return first.charAt(0).toLocaleUpperCase("it") + first.slice(1);
}

export function fillOperatorName(text: string, firstName: string): string {
  return text.replace(new RegExp(PLACEHOLDER_SOURCE, "giu"), firstName);
}

function nameKey(name: string): string {
  return name.normalize("NFKD").replace(/\p{M}/gu, "").toLocaleLowerCase("it");
}

/**
 * The member who introduces themselves in one of our messages ("Ciao! Sono
 * Lorenzo di Sityweb"): the first "sono <nome>" whose name is the first name
 * of exactly one member.
 */
export function introducedMemberId(
  text: string | null | undefined,
  members: ReadonlyArray<{ id: string; name: string | null }>,
): string | null {
  if (!text) return null;
  const byName = new Map<string, string[]>();
  for (const member of members) {
    const first = operatorFirstName(member.name);
    if (!first) continue;
    const key = nameKey(first);
    byName.set(key, [...(byName.get(key) ?? []), member.id]);
  }
  for (const match of text.matchAll(/(?<!\p{L})sono\s+(\p{L}+)/giu)) {
    const ids = byName.get(nameKey(match[1] ?? ""));
    if (ids?.length === 1) return ids[0] ?? null;
  }
  return null;
}

/**
 * An AI text for a lead nobody follows yet that names one of our members for
 * itself ("Ciao! Sono Alessio di …"): the name becomes {operatore}, so the
 * message is signed by whoever sends it.
 */
export function signedByPlaceholder(
  text: string,
  members: ReadonlyArray<{ name: string | null }>,
): string {
  const names = new Set(
    members.flatMap((member) => {
      const first = operatorFirstName(member.name);
      return first ? [nameKey(first)] : [];
    }),
  );
  return text.replace(
    /(?<!\p{L})(sono|mi chiamo)(\s+)(\p{L}+)(?!\p{L})/giu,
    (match, verb: string, space: string, name: string) =>
      names.has(nameKey(name)) ? `${verb}${space}${OPERATOR_PLACEHOLDER}` : match,
  );
}

// Never a placeholder to the lead: {operatore}, {{1}}, {link}, or the [nome]
// and [link] the AI's examples use.
const LEFTOVER_PLACEHOLDER =
  /\{\{[^{}\n]{1,40}\}\}|\{[^{}\n]{1,40}\}|\[\s*(?:nome|name|cognome|azienda|attivit[aà]|operatore|link)(?!\p{L})[^\]\n]{0,30}\]/iu;

/** The first placeholder left in a text for the lead; null when there is none. */
export function leftoverPlaceholder(text: string | null | undefined): string | null {
  return text?.match(LEFTOVER_PLACEHOLDER)?.[0] ?? null;
}

export const OPERATOR_NAME_MISSING_MESSAGE =
  "Manca il nome dell'operatore: il messaggio non è partito. Aggiungi il nome al profilo e riprova.";

export function placeholderLeftMessage(placeholder: string): string {
  return `Il messaggio contiene «${placeholder}», un segnaposto da compilare: non è partito. Correggi il testo e riprova.`;
}

/**
 * The text exactly as it may reach the lead: {operatore} becomes `firstName`.
 * Without a name for it, or with any other placeholder left, there is no text
 * to send, and the reason says why.
 */
export function textForLead(
  text: string,
  firstName: string | null,
):
  | { ok: true; text: string }
  | {
      ok: false;
      code: "OPERATOR_NAME_MISSING" | "PLACEHOLDER_LEFT";
      placeholder: string;
      message: string;
    } {
  if (hasOperatorPlaceholder(text) && !firstName) {
    return {
      ok: false,
      code: "OPERATOR_NAME_MISSING",
      placeholder: OPERATOR_PLACEHOLDER,
      message: OPERATOR_NAME_MISSING_MESSAGE,
    };
  }
  const signed = firstName ? fillOperatorName(text, firstName) : text;
  const left = leftoverPlaceholder(signed);
  return left
    ? {
        ok: false,
        code: "PLACEHOLDER_LEFT",
        placeholder: left,
        message: placeholderLeftMessage(left),
      }
    : { ok: true, text: signed };
}
